import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash-es';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

import clsx from 'clsx';

import { trackV2 } from 'utils/analytics';
import { publicServiceEnv } from 'utils/public-service-env';

import styles from './Video.scss';

const { VIDEOJS_LOG_LEVEL = 'error' } = publicServiceEnv;

// Duration in seconds that a video should play for to consider it a "quality"
// play for analytics purposes.
const QUALITY_PLAY_DURATION = 3;

// Only show error logs from videojs as warning and other logs can get noisy in
// production.
//
videojs.log.level(VIDEOJS_LOG_LEVEL);

function Video({ mediaId, options, className, onReady }) {
  const videoRef = useRef(null);
  const playerRef = useRef(null);

  const [hasPlay, setHasPlay] = useState(false);
  const [hasQualityPlay, setHasQualityPlay] = useState(false);

  useEffect(() => {
    if (!playerRef.current) {
      if (!videoRef.current) {
        return;
      }

      playerRef.current = videojs(videoRef.current, {
        controlBar: {
          children: [
            'playToggle',
            'volumePanel',
            'currentTimeDisplay',
            'progressControl',
            'durationDisplay',
            'fullscreenToggle'
          ]
        },
        ...options
      });

      if (onReady) {
        onReady(playerRef.current);
      }
    } else {
      const player = playerRef.current;

      // As sources usually are a really small object there should not be any
      // issues with using isEqual and deep comparing two objects
      if (!isEqual(player.options().sources, options.sources)) {
        player.src(options.sources);
      }
      if (player.autoplay() !== options.autoplay) {
        player.autoplay(options.autoplay);
      }
      if (player.controls() !== options.controls) {
        player.controls(options.controls);
      }
      if (player.muted() !== options.muted) {
        player.muted(options.muted);
      }
      if (player.loop() !== options.loop) {
        player.loop(options.loop);
      }
    }
  }, [options, onReady]);

  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  const trackAction = useCallback((action) => {
    trackV2({
      action,
      category: 'media',
      page: 'lightbox',
      properties: {
        autoplay: playerRef.current?.autoplay() ? 1 : 0,
        media_id: mediaId
      }
    });
  }, [mediaId]);

  const onPlay = useCallback(() => {
    trackAction('video_start');
    if (!hasPlay) {
      setHasPlay(true);
    }
    if (!playerRef.current) {
      return;
    }

    // Reset quality play state if the video is replayed so we can capture
    // subsequent quality plays. Typically triggered when a user manually
    // replays the video.
    if (hasPlay && playerRef.current.currentTime() < 0.1) {
      setHasQualityPlay(false);
    }
  }, [hasPlay, trackAction]);

  const onTimeUpdate = useCallback(() => {
    if (
      !hasQualityPlay &&
      playerRef.current?.currentTime() >= QUALITY_PLAY_DURATION
    ) {
      trackAction(`video_progress_${QUALITY_PLAY_DURATION}s`);
      setHasQualityPlay(true);
    }
  }, [hasQualityPlay, trackAction]);

  return (
    <div data-vjs-player={true}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        className={clsx(
          'video-js vjs-big-play-centered',
          className,
          styles.videoPlayer
        )}
        playsInline={true}
        autoPlay={false}
        muted={true}
        onPlay={onPlay}
        onEnded={() => trackAction('video_end')}
        onTimeUpdate={onTimeUpdate}
      />
    </div>
  );
}

Video.propTypes = {
  mediaId: PropTypes.string.isRequired,
  // Full list of options can be checked here:
  // https://docs.videojs.com/tutorial-options.html
  // The following is not a complete list so please
  // add more as needed!
  options: PropTypes.shape({
    autoplay: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.oneOf(['muted', 'play', 'any'])
    ]),
    sources: PropTypes.arrayOf(
      PropTypes.shape({
        src: PropTypes.string,
        type: PropTypes.string
      })
    ),
    controls: PropTypes.bool,
    muted: PropTypes.bool,
    loop: PropTypes.bool
  }).isRequired,
  onReady: PropTypes.func,
  className: PropTypes.string
};

export default Video;
